"use client";
import {axiosInstance} from "@/app/api";

type ExpenseTemplateType = "fuel" | "maintenance" | "miscellaneous";

type ExpenseTemplateOption = {
  id?: string | number | null;
  name?: string | number | null;
  regNo?: string | number | null;
  label?: string | number | null;
  value?: string | number | null;
  [key: string]: unknown;
};

type ExpenseTemplateDownloadParams = {
  type: ExpenseTemplateType;
  vehicles?: ExpenseTemplateOption[];
  expenseTypes?: ExpenseTemplateOption[];
  serviceTypes?: ExpenseTemplateOption[];
  vendors?: ExpenseTemplateOption[];
  fuelTypes?: ExpenseTemplateOption[];
  fuelStations?: ExpenseTemplateOption[];
};

type ExpenseTemplateDropdown = {
  header: string;
  namedRange: string;
  title: string;
  values: string[];
};

type ExpenseTemplateValidation =
  | {
      type: "list";
      sqref: string;
      namedRange: string;
      title: string;
    }
  | {
      type: "dateText";
      sqref: string;
      title: string;
    };

const TEMPLATE_ROW_COUNT = 500;
const TEMPLATE_LIST_SHEET_NAME = "Template Lists";
const TEMPLATE_DATE_FORMAT = "yyyy-MM-dd";
const TEMPLATE_DATE_EXAMPLE = "2026-04-27";
const TEMPLATE_TEXT_FORMAT_ID = 49;

const EXPENSE_TEMPLATE_CONFIG: Record<
  ExpenseTemplateType,
  {
    fileName: string;
    sheetName: string;
    headers: string[];
  }
> = {
  miscellaneous: {
    fileName: "miscellaneous-expense-template.xlsx",
    sheetName: "Misc",
    headers: ["Reg No", "Date", "Expense Type", "Total Amount", "Description"]
  },
  maintenance: {
    fileName: "maintenance-expense-template.xlsx",
    sheetName: "Vehicle Expense",
    headers: [
      "Reg No",
      "Date",
      "Total Amount",
      "Service Type",
      "Vendor",
      "Description"
    ]
  },
  fuel: {
    fileName: "fuel-expense-template.xlsx",
    sheetName: "Fuel",
    headers: [
      "Reg No",
      "Date",
      "Qty",
      "Fuel Rate",
      "Fuel Type",
      "Total Amount",
      "Fuel Station",
      "Odometer Reading",
      "Note"
    ]
  }
};

const getTemplateOptionText = (
  option: ExpenseTemplateOption | string | number | null | undefined,
  preferredKeys: string[]
) => {
  if (option == null) return "";
  if (typeof option === "string" || typeof option === "number") {
    return String(option).trim();
  }

  for (const key of preferredKeys) {
    const value = option[key];
    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
};

const normalizeTemplateOptions = (
  options: ExpenseTemplateOption[] | undefined,
  preferredKeys: string[]
) => {
  const seen = new Set<string>();
  const values: string[] = [];

  (options ?? []).forEach(option => {
    if (
      typeof option === "object" &&
      option !== null &&
      "id" in option &&
      String(option.id ?? "").trim() === ""
    ) {
      return;
    }

    const value = getTemplateOptionText(option, preferredKeys);
    if (!value || value.toLowerCase().startsWith("please select")) return;

    const normalizedKey = value.toLowerCase();
    if (seen.has(normalizedKey)) return;

    seen.add(normalizedKey);
    values.push(value);
  });

  return values;
};

const findTemplateHeaderIndex = (headers: string[], targetHeader: string) =>
  headers.findIndex(
    header => header.trim().toLowerCase() === targetHeader.trim().toLowerCase()
  );

const buildExpenseTemplateDropdowns = (
  params: ExpenseTemplateDownloadParams
): ExpenseTemplateDropdown[] => {
  const vehicles = normalizeTemplateOptions(params.vehicles, [
    "regNo",
    "vehicleNo",
    "name",
    "label"
  ]);

  const dropdowns: ExpenseTemplateDropdown[] = [
    {
      header: "Reg No",
      namedRange: "ExpenseTemplateRegNos",
      title: "Reg No",
      values: vehicles
    }
  ];

  if (params.type === "miscellaneous") {
    dropdowns.push({
      header: "Expense Type",
      namedRange: "MiscExpenseTypes",
      title: "Expense Type",
      values: normalizeTemplateOptions(params.expenseTypes, [
        "name",
        "label",
        "value"
      ])
    });
  }

  if (params.type === "maintenance") {
    dropdowns.push(
      {
        header: "Service Type",
        namedRange: "MaintenanceServiceTypes",
        title: "Service Type",
        values: normalizeTemplateOptions(params.serviceTypes, [
          "name",
          "label",
          "value"
        ])
      },
      {
        header: "Vendor",
        namedRange: "MaintenanceVendors",
        title: "Vendor",
        values: normalizeTemplateOptions(params.vendors, [
          "name",
          "label",
          "value"
        ])
      }
    );
  }

  if (params.type === "fuel") {
    dropdowns.push(
      {
        header: "Fuel Type",
        namedRange: "FuelTypes",
        title: "Fuel Type",
        values: normalizeTemplateOptions(params.fuelTypes, [
          "name",
          "label",
          "value"
        ])
      },
      {
        header: "Fuel Station",
        namedRange: "FuelStations",
        title: "Fuel Station",
        values: normalizeTemplateOptions(params.fuelStations, [
          "name",
          "label",
          "value"
        ])
      }
    );
  }

  return dropdowns.filter(dropdown => dropdown.values.length > 0);
};

const escapeXml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const toUint8Array = (data: ArrayBuffer | Uint8Array | string | number[]) => {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (Array.isArray(data)) return new Uint8Array(data);

  const output = new Uint8Array(data.length);
  for (let idx = 0; idx < data.length; idx += 1) {
    output[idx] = data.charCodeAt(idx) & 0xff;
  }
  return output;
};

const getCfbFileByPath = (cfb: any, path: string) => {
  const index = cfb.FullPaths.findIndex((filePath: string) =>
    filePath.endsWith(path)
  );
  return index >= 0 ? cfb.FileIndex[index] : null;
};

const addExpenseTemplateDateTextStyle = (cfb: any, dateColumnIndex: number) => {
  const stylesFile = getCfbFileByPath(cfb, "xl/styles.xml");
  const worksheetFile = getCfbFileByPath(cfb, "xl/worksheets/sheet1.xml");

  if (!stylesFile?.content || !worksheetFile?.content) return;

  const decoder = new TextDecoder();
  const encoder = new TextEncoder();
  let stylesXml = decoder.decode(toUint8Array(stylesFile.content));
  let worksheetXml = decoder.decode(toUint8Array(worksheetFile.content));
  const cellXfsCountMatch = stylesXml.match(/<cellXfs count="(\d+)"/);
  const dateStyleIndex = cellXfsCountMatch ? Number(cellXfsCountMatch[1]) : 1;
  const dateStyleXml = `<xf numFmtId="${TEMPLATE_TEXT_FORMAT_ID}" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`;

  stylesXml = stylesXml
    .replace(/<cellXfs count="(\d+)"/, (_, count) => {
      return `<cellXfs count="${Number(count) + 1}"`;
    })
    .replace("</cellXfs>", `${dateStyleXml}</cellXfs>`);

  const dateColumnNumber = dateColumnIndex + 1;
  const dateColumnRegex = new RegExp(
    `<col\\b(?=[^>]*\\bmin="${dateColumnNumber}")(?=[^>]*\\bmax="${dateColumnNumber}")[^>]*/>`
  );

  if (dateColumnRegex.test(worksheetXml)) {
    worksheetXml = worksheetXml.replace(dateColumnRegex, columnXml =>
      columnXml
        .replace(/\sstyle="[^"]*"/, "")
        .replace(/\/>$/, ` style="${dateStyleIndex}"/>`)
    );
  } else if (worksheetXml.includes("<cols>")) {
    worksheetXml = worksheetXml.replace(
      "<cols>",
      `<cols><col min="${dateColumnNumber}" max="${dateColumnNumber}" style="${dateStyleIndex}"/>`
    );
  } else {
    worksheetXml = worksheetXml.replace(
      "<sheetData",
      `<cols><col min="${dateColumnNumber}" max="${dateColumnNumber}" style="${dateStyleIndex}"/></cols><sheetData`
    );
  }

  stylesFile.content = encoder.encode(stylesXml);
  stylesFile.size = stylesFile.content.length;
  worksheetFile.content = encoder.encode(worksheetXml);
  worksheetFile.size = worksheetFile.content.length;
};

const injectExpenseTemplateValidations = (
  cfb: any,
  validations: ExpenseTemplateValidation[]
) => {
  if (!validations.length) return;

  const worksheetFile = getCfbFileByPath(cfb, "xl/worksheets/sheet1.xml");
  if (!worksheetFile?.content) {
    throw new Error("Unable to prepare template dropdowns.");
  }

  const decoder = new TextDecoder();
  const encoder = new TextEncoder();
  const xml = decoder.decode(toUint8Array(worksheetFile.content));
  const validationXml = `<dataValidations count="${validations.length}">${validations
    .map(validation => {
      if (validation.type === "dateText") {
        const firstCell = validation.sqref.split(":")[0];

        return `<dataValidation type="custom" allowBlank="1" showInputMessage="1" showErrorMessage="1" sqref="${escapeXml(
          validation.sqref
        )}"><formula1>${escapeXml(
          `AND(LEN(${firstCell})=10,MID(${firstCell},5,1)="-",MID(${firstCell},8,1)="-",ISNUMBER(DATEVALUE(${firstCell})))`
        )}</formula1><promptTitle>${escapeXml(
          validation.title
        )}</promptTitle><prompt>${escapeXml(
          `Enter as text in ${TEMPLATE_DATE_FORMAT} format, for example ${TEMPLATE_DATE_EXAMPLE}.`
        )}</prompt><errorTitle>${escapeXml(
          "Invalid date"
        )}</errorTitle><error>${escapeXml(
          `Please enter the date as ${TEMPLATE_DATE_FORMAT}, for example ${TEMPLATE_DATE_EXAMPLE}.`
        )}</error></dataValidation>`;
      }

      return `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" sqref="${escapeXml(
        validation.sqref
      )}"><formula1>${escapeXml(
        validation.namedRange
      )}</formula1><promptTitle>${escapeXml(
        validation.title
      )}</promptTitle><prompt>${escapeXml(
        "Choose a value from the dropdown."
      )}</prompt><errorTitle>${escapeXml(
        "Invalid value"
      )}</errorTitle><error>${escapeXml(
        "Please choose a value from the dropdown."
      )}</error></dataValidation>`;
    })
    .join("")}</dataValidations>`;

  const insertBefore = xml.includes("<hyperlinks")
    ? "<hyperlinks"
    : xml.includes("<ignoredErrors")
      ? "<ignoredErrors"
      : "</worksheet>";
  const nextXml = xml.replace(insertBefore, `${validationXml}${insertBefore}`);

  worksheetFile.content = encoder.encode(nextXml);
  worksheetFile.size = worksheetFile.content.length;
};

const toBlobArrayBuffer = (data: Uint8Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(data.byteLength);
  new Uint8Array(buffer).set(data);
  return buffer;
};

const downloadBlob = (blobPart: Uint8Array, fileName: string) => {
  const blob = new Blob([toBlobArrayBuffer(blobPart)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const downloadExpenseTemplate = async (
  params: ExpenseTemplateDownloadParams
): Promise<void> => {
  const config = EXPENSE_TEMPLATE_CONFIG[params.type];
  const dropdowns = buildExpenseTemplateDropdowns(params);
  const {CFB, utils, write} = await import("xlsx");
  const workbook = utils.book_new();
  const worksheet = utils.aoa_to_sheet([config.headers]);
  const lastColumn = utils.encode_col(config.headers.length - 1);
  const dateColumnIndex = config.headers.findIndex(header => header === "Date");

  worksheet["!ref"] = `A1:${lastColumn}${TEMPLATE_ROW_COUNT + 1}`;
  worksheet["!cols"] = config.headers.map(header => ({
    wch: Math.max(header === "Date" ? 16 : 14, header.length + 4)
  }));

  utils.book_append_sheet(workbook, worksheet, config.sheetName);

  const maxListLength = Math.max(
    1,
    ...dropdowns.map(dropdown => dropdown.values.length)
  );
  const listRows = Array.from({length: maxListLength + 1}, (_, rowIndex) =>
    dropdowns.map(dropdown =>
      rowIndex === 0 ? dropdown.title : (dropdown.values[rowIndex - 1] ?? "")
    )
  );
  const listSheet = utils.aoa_to_sheet(listRows);

  listSheet["!cols"] = dropdowns.map(dropdown => ({
    wch: Math.max(18, dropdown.title.length + 4)
  }));
  utils.book_append_sheet(workbook, listSheet, TEMPLATE_LIST_SHEET_NAME);

  workbook.Workbook = {
    ...(workbook.Workbook ?? {}),
    Sheets: workbook.SheetNames.map((_, index) => ({
      Hidden: index === 1 ? 1 : 0
    })),
    Names: dropdowns.map((dropdown, index) => {
      const column = utils.encode_col(index);
      return {
        Name: dropdown.namedRange,
        Ref: `'${TEMPLATE_LIST_SHEET_NAME}'!$${column}$2:$${column}$${
          dropdown.values.length + 1
        }`
      };
    })
  };

  const validations = dropdowns
    .map(dropdown => {
      const headerIndex = findTemplateHeaderIndex(
        config.headers,
        dropdown.header
      );
      if (headerIndex < 0) return null;

      const column = utils.encode_col(headerIndex);
      return {
        sqref: `${column}2:${column}${TEMPLATE_ROW_COUNT + 1}`,
        namedRange: dropdown.namedRange,
        type: "list" as const,
        title: dropdown.title
      };
    })
    .filter(Boolean) as ExpenseTemplateValidation[];

  if (dateColumnIndex >= 0) {
    const dateColumn = utils.encode_col(dateColumnIndex);
    validations.push({
      type: "dateText",
      sqref: `${dateColumn}2:${dateColumn}${TEMPLATE_ROW_COUNT + 1}`,
      title: "Date Format"
    });
  }

  const workbookData = write(workbook, {
    type: "array",
    bookType: "xlsx"
  }) as ArrayBuffer | Uint8Array | string | number[];
  const cfb = CFB.read(toUint8Array(workbookData), {type: "array"});

  if (dateColumnIndex >= 0) {
    addExpenseTemplateDateTextStyle(cfb, dateColumnIndex);
  }
  injectExpenseTemplateValidations(cfb, validations);

  const output = CFB.write(cfb, {
    type: "array",
    fileType: "zip"
  }) as ArrayBuffer | Uint8Array | string | number[];

  downloadBlob(toUint8Array(output), config.fileName);
};

const getvehicles = async (requestData: any): Promise<any> => {
  const url = "/api/fleet/vehicles/list";
  return await axiosInstance().post(url, requestData);
};

const getexpensesummary = async (
  userid: any,
  requestdata: any
): Promise<any> => {
  const url = "/api/fleet/expense/summary?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const getoverallexpense = async (
  userid: any,
  requestdata: any
): Promise<any> => {
  const url = "/api/fleet/allExpenses/list";
  return await axiosInstance().post(url, requestdata);
};

const getgroupvehicle = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/allExpenses/vehicleGrouped?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const getgroupdate = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/allExpenses/dateGrouped?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const getgroupexpense = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/allExpenses/expenseGrouped?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const getfuelexpense = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/fuelExpense/list";
  return await axiosInstance().post(url, requestdata);
};

const getmaintance = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/maintenanceExpense/list";
  return await axiosInstance().post(url, requestdata);
};

const getmiscellaneous = async (
  userid: any,
  requestdata: any
): Promise<any> => {
  const url = "/api/fleet/miscExpenses/list";
  return await axiosInstance().post(url, requestdata);
};

const addexpenselist = async (userid: any, data: any): Promise<any> => {
  const url = "/api/fleet/expense/list/add?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const addfuelexpense = async (userid: any, data: any): Promise<any> => {
  var url: string = "/api/fleet/fuelExpense/add?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const updatefuelexpense = async (userid: any, data: any): Promise<any> => {
  var url: string = "/api/fleet/fuelExpense/edit?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const deletefuelexpense = async (
  userid: any,
  requestdata: any
): Promise<any> => {
  const url = "/api/fleet/fuelExpense/delete?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const getexpenseview = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/allExpenses/readById?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const addmaintenanceexpense = async (userid: any, data: any): Promise<any> => {
  var url: string = "/api/fleet/maintenanceExpense/add?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const updatemaintenanceexpense = async (
  userid: any,
  data: any
): Promise<any> => {
  var url: string = "/api/fleet/maintenanceExpense/edit?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const deletemaintenanceexpense = async (
  userid: any,
  requestdata: any
): Promise<any> => {
  const url = "/api/fleet/maintenanceExpense/delete?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const addmiscellaneousexpense = async (
  userid: any,
  data: any
): Promise<any> => {
  var url: string = "/api/fleet/miscExpenses/add?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const updatemiscellaneousexpense = async (
  userid: any,
  data: any
): Promise<any> => {
  var url: string = "/api/fleet/miscExpenses/edit?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const deletemiscellaneousexpense = async (
  userid: any,
  requestdata: any
): Promise<any> => {
  const url = "/api/fleet/miscExpenses/delete?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const documentupload = async (userid: any, data: any): Promise<any> => {
  var url: string = "/api/fleet/document/add?loginUserId=" + userid;
  return await axiosInstance().post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
};

const deletedocument = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/document/delete?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

const deleteExpense = async (userid: any, requestdata: any): Promise<any> => {
  const url = "/api/fleet/expenses/delete?loginUserId=" + userid;
  return await axiosInstance().post(url, requestdata);
};

export const ExpenseService = {
  getvehicles,
  getexpensesummary,
  getoverallexpense,
  getgroupvehicle,
  getgroupdate,
  getgroupexpense,
  getfuelexpense,
  getmaintance,
  getmiscellaneous,
  addexpenselist,
  addfuelexpense,
  updatefuelexpense,
  deletefuelexpense,
  getexpenseview,
  addmaintenanceexpense,
  updatemaintenanceexpense,
  deletemaintenanceexpense,
  addmiscellaneousexpense,
  updatemiscellaneousexpense,
  deletemiscellaneousexpense,
  documentupload,
  deletedocument,
  deleteExpense,
  downloadExpenseTemplate
};
